import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LogOut, BarChart3, Scan, Upload, User, Shield } from 'lucide-react';
import { useAuth } from '../App';
import { supabase } from '../lib/supabase';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, userRole, signOut } = useAuth();
  const location = useLocation();
  const [showProfileModal, setShowProfileModal] = useState(false);

  // Hide navbar on scanner/count screens to save space on mobile
  const isCountingScreen = location.pathname.includes('/count/');
  
  return (
    <div className="min-h-screen bg-base-200">
      {!isCountingScreen && (
        <div className="navbar bg-base-100 shadow-lg">
          <div className="flex-1">
            <Link to="/" className="btn btn-ghost normal-case text-xl">
              <BarChart3 className="w-6 h-6 mr-2" />
              HeliosX
            </Link>
          </div>
          <div className="flex-none">
            <ul className="menu menu-horizontal px-1">
              {userRole === 'admin' && (
                <li>
                  <Link 
                    to="/admin" 
                    className={`btn btn-ghost ${location.pathname === '/admin' ? 'btn-active' : ''}`}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Admin
                  </Link>
                </li>
              )}
              <li>
                <Link 
                  to="/scanner" 
                  className={`btn btn-ghost ${location.pathname === '/scanner' ? 'btn-active' : ''}`}
                >
                  <Scan className="w-4 h-4 mr-2" />
                  Scanner
                </Link>
              </li>
            </ul>
            <div className="dropdown dropdown-end">
              <label tabIndex={0} className="btn btn-ghost">
                <User className="w-4 h-4 mr-2" />
                Profile
              </label>
              <ul tabIndex={0} className="menu menu-sm dropdown-content mt-3 z-[1] p-2 shadow bg-base-100 rounded-box w-52">
                <li>
                  <a className="justify-between" onClick={() => setShowProfileModal(true)}>
                    <div className="flex items-center">
                      <User className="w-4 h-4 mr-2" />
                      Profile
                    </div>
                    <span className={`badge badge-sm ${userRole === 'admin' ? 'badge-primary' : 'badge-secondary'}`}>
                      {userRole}
                    </span>
                  </a>
                </li>
                <li>
                  <a onClick={signOut}>
                    <LogOut className="w-4 h-4" />
                    Sign out
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      )}
      <main className={isCountingScreen ? "p-2" : "container mx-auto px-4 py-8"}>
        {children}
      </main>

      {/* Profile Modal */}
      {showProfileModal && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg mb-4">User Profile</h3>
            
            <div className="space-y-4">
              <div className="flex items-center justify-center mb-6">
                <div className="avatar">
                  <div className="w-20 rounded-full bg-primary text-primary-content flex items-center justify-center text-2xl font-bold">
                    {user?.email?.charAt(0).toUpperCase()}
                  </div>
                </div>
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text">Email</span>
                </label>
                <input
                  type="text"
                  className="input input-bordered w-full"
                  value={user?.email || ''}
                  disabled
                />
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text">Role</span>
                </label>
                <div className="flex items-center gap-2">
                  <Shield className={`w-5 h-5 ${userRole === 'admin' ? 'text-primary' : 'text-secondary'}`} />
                  <span className={`badge ${userRole === 'admin' ? 'badge-primary' : 'badge-secondary'} badge-lg`}>
                    {userRole === 'admin' ? 'Administrator' : 'User'}
                  </span>
                </div>
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text">User ID</span>
                </label>
                <input
                  type="text"
                  className="input input-bordered w-full font-mono text-sm"
                  value={user?.id || ''}
                  disabled
                />
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text">Permissions</span>
                </label>
                <div className="bg-base-200 p-3 rounded">
                  <ul className="text-sm space-y-1">
                    {userRole === 'admin' ? (
                      <>
                        <li className="flex items-center gap-2">
                          <span className="text-success">✓</span>
                          Create and manage count sessions
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-success">✓</span>
                          Upload CSV files
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-success">✓</span>
                          View all sessions and results
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-success">✓</span>
                          Manage user roles
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-success">✓</span>
                          Perform counting tasks
                        </li>
                      </>
                    ) : (
                      <>
                        <li className="flex items-center gap-2">
                          <span className="text-error">✗</span>
                          Create and manage count sessions
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-error">✗</span>
                          Upload CSV files
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-success">✓</span>
                          View assigned sessions
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-success">✓</span>
                          Perform counting tasks
                        </li>
                      </>
                    )}
                  </ul>
                </div>
              </div>

              {userRole !== 'admin' && (
                <div className="alert alert-info">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="stroke-current shrink-0 w-6 h-6"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                  <span>Contact your administrator to request admin permissions.</span>
                </div>
              )}
            </div>
            
            <div className="modal-action">
              <button 
                className="btn btn-primary"
                onClick={async () => {
                  // Force refresh user role
                  if (user?.id) {
                    try {
                      const { data, error } = await supabase
                        .from('profiles')
                        .select('role')
                        .eq('id', user.id)
                        .single();
                      
                      if (error) {
                        console.error('Error refreshing role:', error);
                        alert('Error refreshing role. Please try logging out and back in.');
                      } else {
                        console.log('Current role in DB:', data?.role);
                        alert(`Role refreshed! Database shows: ${data?.role}. Please refresh the page.`);
                        window.location.reload();
                      }
                    } catch (error) {
                      console.error('Error refreshing role:', error);
                      alert('Error refreshing role. Please try logging out and back in.');
                    }
                  }
                }}
              >
                Refresh Role
              </button>
              <button 
                className="btn btn-ghost"
                onClick={() => setShowProfileModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout; 